import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { MeResponse } from '@/types/api';

// Авторизованное состояние пользователя в RTK store. Эквивалентно полю state
// в legacy src/contexts/AuthContext.tsx, но единое для всех новых страниц.
//
// Single source of truth для auth — это HMAC-cookies (auth_key + auth_user_id +
// auth_2af). RTK store кэширует производное состояние для UI. При старте
// приложения и после login/check2fa cookies обновляются → useMe re-fetches /me →
// store обновляется через extraReducers (см. ниже).
export type AuthStatus = 'unknown' | 'guest' | 'pending-2fa' | 'authenticated';

export interface Pending2FA {
  temporaryToken: string;
  auth_user_id: number;
  rememberMe: boolean;
}

export interface AuthState {
  status: AuthStatus;
  me: MeResponse | null;
  pending2fa: Pending2FA | null;
}

const initialState: AuthState = {
  status: 'unknown',
  me: null,
  pending2fa: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setGuest(state) {
      state.status = 'guest';
      state.me = null;
      state.pending2fa = null;
    },
    setPending2FA(state, action: PayloadAction<Pending2FA>) {
      state.status = 'pending-2fa';
      state.me = null;
      state.pending2fa = action.payload;
    },
    setAuthenticated(state, action: PayloadAction<MeResponse | null>) {
      state.status = 'authenticated';
      state.me = action.payload;
      state.pending2fa = null;
    },
    setMe(state, action: PayloadAction<MeResponse>) {
      // Обновление /me без перевода статуса (для последующих refetch).
      state.me = action.payload;
      if (state.status === 'unknown') {
        state.status = 'authenticated';
      }
    },
  },
});

export const { setGuest, setPending2FA, setAuthenticated, setMe } =
  authSlice.actions;

export default authSlice.reducer;
